// The application root: providers on the outside, routes on the inside.

import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { I18nProvider } from './i18n/I18nProvider';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { Toaster } from './components/common/Toaster';
import { AppRoutes } from './routes/AppRoutes';
import { useAuthStore } from './stores/authStore';
import { startSyncEngine } from './lib/syncEngine';
import { startSessionKeeper, stopSessionKeeper } from './lib/sessionKeeper';
import { registerServiceWorker } from './lib/serviceWorker';
import { UpdatePrompt, useUpdateAvailable } from './components/common/UpdatePrompt';
import { SyncedTicketPrints } from './components/pos/SyncedTicketPrints';

/// Asks the server once, on start-up, whether the browser still holds a valid
/// session. Until that answers, the guards show a brief checking state rather
/// than flashing the login page at somebody who is in fact still signed in.
function SessionBootstrap({ children }: { children: React.ReactNode }) {
  const bootstrap = useAuthStore((state) => state.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return <>{children}</>;
}

/// Starts the offline machinery once, for the whole session.
///
/// Both of these are deliberately outside the router: the connection watcher
/// has to keep running while somebody navigates, and the service worker
/// registers itself after the first paint so it never competes with the till's
/// own opening speed.
function OfflineBootstrap({ children }: { children: React.ReactNode }) {
  const markUpdateAvailable = useUpdateAvailable();
  const status = useAuthStore((state) => state.status);

  useEffect(() => {
    startSyncEngine();
    registerServiceWorker(markUpdateAvailable);
  }, [markUpdateAvailable]);

  // The session is topped up only while somebody is signed in AND using the
  // till. Started here rather than in the store so it stops cleanly when the
  // application unmounts, and so a till left on overnight in an empty shop
  // lets its session lapse rather than renewing it until morning.
  useEffect(() => {
    if (status !== 'authenticated') return;

    startSessionKeeper({
      onIdle: () => {
        // Genuinely untouched for the whole limit. This is the ONE place the
        // keeper ends a session, and it is not a network condition.
        void useAuthStore.getState().logout();
      },
    });

    return () => stopSessionKeeper();
  }, [status]);

  return <>{children}</>;
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          <SessionBootstrap>
            <OfflineBootstrap>
              {/* The future flags opt in to React Router v7's behaviour now.
                  They remove the upgrade warnings and mean the eventual move to
                  v7 is a version bump rather than a rewrite. */}
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <AppRoutes />
                {/* Mounted once, above the routes, so a confirmation survives a
                    navigation that happens right after it is shown. */}
                <Toaster />
                {/* Offered, never applied by itself: a till must not reload
                    mid-order to install a new version. */}
                <UpdatePrompt />
                {/* Bench tickets owed by sales that were rung up offline and
                    have just been recorded; printed once, wherever the till is. */}
                <SyncedTicketPrints />
              </BrowserRouter>
            </OfflineBootstrap>
          </SessionBootstrap>
        </I18nProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
